/**
 * Admin / Super Admin "Warranty + Pest Catalog" tab — The Indian Pest Control.
 * Sub-tabs: Certificates (list + issue + PDF) · Pest Catalog (super admin CRUD)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ShieldCheck, Plus, Search, Download, Trash2, Loader2, X, CheckCircle2,
  AlertCircle, Bug, Edit2, ToggleRight, ToggleLeft, FileText, Calendar,
  Settings, History, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const STATUS_META = {
  active: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2, label: 'Active' },
  expired: { cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertCircle, label: 'Expired' },
  void: { cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: X, label: 'Void' },
  cancelled: { cls: 'bg-rose-100 text-rose-800 border-rose-300', Icon: X, label: 'Cancelled' },
  stopped: { cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: AlertCircle, label: 'Stopped (on hold)' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.active;
  const Icon = meta.Icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}><Icon size={10} /> {meta.label}</span>;
}

export default function WarrantyAdminTab({ api, isSuper }) {
  const [tab, setTab] = useState('certs');
  const [autoIssue, setAutoIssue] = useState(false);
  useEffect(() => { api('GET', '/admin/warranty-settings').then((r) => setAutoIssue(Boolean(r.data.auto_issue_on_completion))).catch(() => {}); }, [api]);
  const toggleAutoIssue = async () => { const next = !autoIssue; try { await api('PUT', '/super-admin/warranty-settings', { auto_issue_on_completion: next }); setAutoIssue(next); } catch { alert('Could not update automatic warranty setting'); } };
  return (
    <div data-testid="warranty-admin-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-700" /> Warranty Management</h1>
          <p className="text-sm text-slate-500">The Indian Pest Control · Manage pest catalog & coverage periods.</p>
        </div>
        {isSuper && <button type="button" onClick={toggleAutoIssue} className={`rounded-md border px-3 py-2 text-xs font-bold ${autoIssue ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-slate-300 bg-white text-slate-600'}`} data-testid="warranty-auto-issue-toggle">Auto-generate after completion: {autoIssue ? 'ON' : 'OFF'}</button>}
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <TabBtn id="certs" active={tab} onClick={setTab} Icon={ShieldCheck} label="Certificates" />
        {isSuper && <TabBtn id="catalog" active={tab} onClick={setTab} Icon={Bug} label="Pest Catalog & Warranty Rules" />}
      </div>

      {tab === 'certs' && <CertificatesPanel api={api} isSuper={isSuper} />}
      {tab === 'catalog' && isSuper && <PestCatalogPanel api={api} />}
    </div>
  );
}

function TabBtn({ id, active, onClick, Icon, label }) {
  const on = active === id;
  return (
    <button onClick={() => onClick(id)} className={`px-4 py-2 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 ${on ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`war-tab-${id}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

// =====================================================
// CERTIFICATES PANEL
// =====================================================
function CertificatesPanel({ api, isSuper }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all|today|7d|30d|custom
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [err, setErr] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [pestList, setPestList] = useState([]);
  const [managing, setManaging] = useState(null); // warranty to manage
  const [editingNum, setEditingNum] = useState(null); // warranty whose number is being edited

  const today = () => new Date().toISOString().slice(0, 10);
  const minusDays = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

  const dateRange = useMemo(() => {
    if (dateFilter === 'today') return { from: today(), to: today() };
    if (dateFilter === '7d') return { from: minusDays(7), to: today() };
    if (dateFilter === '30d') return { from: minusDays(30), to: today() };
    if (dateFilter === 'custom') return { from: dateFrom, to: dateTo };
    return { from: '', to: '' };
  }, [dateFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', '/admin/warranties' + (search ? `?search=${encodeURIComponent(search)}` : ''));
      let list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Apply client-side date filter (start_date / issued_at)
      if (dateRange.from || dateRange.to) {
        list = list.filter((w) => {
          const d = (w.start_date || w.treatment_date || w.issued_at || '').slice(0, 10);
          if (!d) return false;
          if (dateRange.from && d < dateRange.from) return false;
          if (dateRange.to && d > dateRange.to) return false;
          return true;
        });
      }
      setRows(list);
      const pc = await api('GET', '/admin/pest-catalog');
      setPestList(pc.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api, search, dateRange]);
  useEffect(() => { load(); }, [load]);

  const downloadPDF = async (w) => {
    try {
      const r = await api('GET', `/admin/warranties/${w.id}/pdf`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `TheIndianPestControl_Warranty_${(w.warranty_number || w.id).replace(/.*-/, '')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Permanently delete warranty ${w.warranty_number || w.id}?\n\nThis cannot be undone. The certificate record will be removed. Associated booking/invoice are NOT deleted.`)) return;
    try { await api('DELETE', `/admin/warranties/${w.id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <>
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by warranty #, booking ID, mobile, email, or customer name…" className="pl-9 h-10" data-testid="war-search" />
          </div>
          <Button onClick={() => setIssuing(true)} className="bg-emerald-700 hover:bg-emerald-800" size="sm" data-testid="war-new"><Plus size={14} className="mr-1" /> Issue Warranty</Button>
        </div>
        {/* Time filter pills */}
        <div className="flex flex-wrap gap-1.5 items-center" data-testid="war-time-filter">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Date:</span>
          {[['all', 'All'], ['today', 'Today'], ['7d', '7 Days'], ['30d', '30 Days'], ['custom', 'Custom']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setDateFilter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${dateFilter === k ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-testid={`war-date-${k}`}>{l}</button>
          ))}
          {dateFilter === 'custom' && (
            <>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-auto" data-testid="war-date-from" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-auto" data-testid="war-date-to" />
            </>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-3">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5">Warranty #</th>
              <th className="text-left px-3 py-2.5">Customer</th>
              <th className="text-left px-3 py-2.5">Pest</th>
              <th className="text-left px-3 py-2.5">Coverage</th>
              <th className="text-left px-3 py-2.5">Valid Until</th>
              <th className="text-center px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-10 text-center text-slate-400"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400">No warranties yet.</td></tr>}
            {!loading && rows.map(w => {
              const isTipc = (w.warranty_number || '').startsWith('TIPC-WAR');
              return (
                <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`war-row-${w.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-900">{w.warranty_number || w.id?.slice(0, 8)}</td>
                  <td className="px-3 py-2"><div className="text-slate-900 font-medium">{w.customer_name}</div><div className="text-[11px] text-slate-500">{w.customer_phone}</div></td>
                  <td className="px-3 py-2 text-slate-700">{w.pest_name || w.service_label || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">{w.warranty_value != null ? `${w.warranty_value} ${w.warranty_unit}` : (w.days ? `${w.days} days` : '—')}</td>
                  <td className="px-3 py-2 text-slate-700">{fmtDate(w.end_date || w.ends_at)}</td>
                  <td className="px-3 py-2 text-center"><StatusBadge status={w.status} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {isTipc && (
                        <Button variant="ghost" size="sm" onClick={() => setManaging(w)} title="Manage warranty (extend/reduce/cancel/stop)" data-testid={`war-manage-${w.id}`} className="text-indigo-600 hover:bg-indigo-50">
                          <Settings size={13} />
                        </Button>
                      )}
                      {isTipc && <Button variant="ghost" size="sm" onClick={() => downloadPDF(w)} title="Download PDF" data-testid={`war-pdf-${w.id}`}><Download size={13} /></Button>}
                      {isTipc && <Button variant="ghost" size="sm" onClick={() => setEditingNum(w)} title="Edit warranty number" data-testid={`war-edit-num-${w.id}`} className="text-slate-600 hover:bg-slate-100"><Edit2 size={13} /></Button>}
                      {isTipc && <Button variant="ghost" size="sm" onClick={() => remove(w)} title="Delete warranty" data-testid={`war-del-${w.id}`} className="text-rose-600 hover:bg-rose-50"><Trash2 size={13} /></Button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {issuing && <IssueWarrantyModal api={api} pestList={pestList} onClose={() => setIssuing(false)} onIssued={() => { setIssuing(false); load(); }} />}
      {managing && <ManageWarrantyModal api={api} warranty={managing} onClose={() => setManaging(null)} onChanged={() => { setManaging(null); load(); }} />}
      {editingNum && <EditNumberModal api={api} warranty={editingNum} onClose={() => setEditingNum(null)} onSaved={() => { setEditingNum(null); load(); }} />}
    </>
  );
}

function EditNumberModal({ api, warranty, onClose, onSaved }) {
  const [value, setValue] = useState(warranty.warranty_number || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    const v = (value || '').trim();
    if (!v) { setErr('Warranty number is required'); return; }
    if (v === warranty.warranty_number) { onClose(); return; }
    setBusy(true);
    try {
      await api('PATCH', `/admin/warranties/${warranty.id}/number`, { warranty_number: v });
      onSaved();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || 'Could not update warranty number');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="war-edit-num-modal">
      <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Edit2 size={16} className="text-emerald-700" /> Edit Warranty Number</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="war-edit-num-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">Customer: <span className="font-medium text-slate-700">{warranty.customer_name}</span> · Current: <span className="font-mono">{warranty.warranty_number}</span></div>
          <div>
            <Label>Warranty Number *</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g., TIPC-WAR-2026-000123" className="font-mono" autoFocus data-testid="war-edit-num-input" />
            <p className="text-[11px] text-slate-400 mt-1">Must be unique. The certificate PDF and all linked booking/invoice views update instantly.</p>
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="war-edit-num-error">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={onClose} data-testid="war-edit-num-cancel">Cancel</Button>
          <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="war-edit-num-save">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving…</> : 'Save Number'}
          </Button>
        </div>
      </form>
    </div>
  );
}


function IssueWarrantyModal({ api, pestList, onClose, onIssued }) {
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '', customer_address: '',
    pest_slug: pestList[0]?.slug || '', treatment_date: new Date().toISOString().slice(0, 10),
    override_value: '', override_unit: '', sector: 'residential',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const selectedPest = pestList.find((p) => p.slug === form.pest_slug);
  const effectiveValue = Number(form.override_value || selectedPest?.warranty_value || 0);
  const effectiveUnit = form.override_unit || selectedPest?.warranty_unit || 'days';
  const expiryPreview = (() => { const d = new Date(`${form.treatment_date || new Date().toISOString().slice(0, 10)}T00:00:00`); if (!effectiveValue) return null; if (effectiveUnit === 'days') d.setDate(d.getDate() + effectiveValue); if (effectiveUnit === 'months') d.setMonth(d.getMonth() + effectiveValue); if (effectiveUnit === 'years') d.setFullYear(d.getFullYear() + effectiveValue); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name?.trim() || !form.customer_phone?.trim() || !form.pest_slug) {
      setErr('Customer name, phone and pest are required'); return;
    }
    setBusy(true); setErr('');
    try {
      const payload = { ...form };
      if (!payload.override_value) { delete payload.override_value; delete payload.override_unit; }
      else { payload.override_value = Number(payload.override_value); }
      await api('POST', '/admin/warranties', payload);
      onIssued();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="war-issue-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-700 text-white">
          <div><div className="text-xs opacity-90">The Indian Pest Control</div><div className="font-bold">Issue Warranty Certificate</div></div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Customer Name *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="war-cust-name" /></div>
            <div><Label>Mobile *</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="war-cust-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="war-cust-email" /></div>
            <div><Label>Address</Label><Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="war-cust-addr" /></div>
            <div>
              <Label>Pest Type *</Label>
              <select value={form.pest_slug} onChange={(e) => { const pest = pestList.find((p) => p.slug === e.target.value); setForm({ ...form, pest_slug: e.target.value, override_value: pest?.warranty_value || '', override_unit: pest?.warranty_unit || 'days' }); }} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="war-pest">
                <option value="">— select —</option>
                {pestList.filter(p => p.active && p.warranty_value > 0 && (p.sector || 'residential') === form.sector).map(p => (
                  <option key={p.slug} value={p.slug}>{p.name} ({p.warranty_value} {p.warranty_unit})</option>
                ))}
              </select>
            </div>
            <div><Label>Category *</Label><select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value, pest_slug: '', override_value: '', override_unit: '' })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="war-sector"><option value="residential">Residential</option><option value="commercial">Commercial</option><option value="industrial">Industrial</option></select></div>
            <div><Label>Treatment Date</Label><Input type="date" value={form.treatment_date} onChange={(e) => setForm({ ...form, treatment_date: e.target.value })} data-testid="war-tx-date" /></div>
          </div>
          <details className="bg-slate-50 rounded-lg p-3" open>
            <summary className="text-sm font-semibold text-slate-700 cursor-pointer">Coverage duration — increase or reduce before issuing</summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label className="text-xs">Value</Label><Input type="number" min="1" value={form.override_value} onChange={(e) => setForm({ ...form, override_value: e.target.value })} placeholder="e.g., 365" data-testid="war-override-val" /></div>
              <div><Label className="text-xs">Unit</Label>
                <select value={form.override_unit} onChange={(e) => setForm({ ...form, override_unit: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="war-override-unit">
                  <option value="">use pest default</option>
                  <option value="days">days</option><option value="months">months</option><option value="years">years</option>
                </select>
              </div>
            </div>
            <div className="mt-3 border-l-2 border-emerald-600 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="war-expiry-preview"><b>Certificate coverage:</b> {effectiveValue || '—'} {effectiveUnit} · <b>Expiry:</b> {expiryPreview || 'Select a pest and treatment date'}</div>
          </details>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="war-issue-submit">
            {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Issuing…</> : 'Issue Certificate'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// =====================================================
// PEST CATALOG (SUPER ADMIN ONLY)
// =====================================================
function PestCatalogPanel({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | pest object
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', '/admin/pest-catalog?include_inactive=true');
      setRows(r.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    try { await api('PATCH', `/super-admin/pest-catalog/${p.id}`, { active: !p.active }); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? Existing warranties under this pest stay valid.`)) return;
    try { await api('DELETE', `/super-admin/pest-catalog/${p.id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="text-sm text-slate-600">Configure pest types — these populate dropdowns on Invoices, Warranties, and the auto-warranty engine.</div>
        <span className="text-xs text-slate-500">Add rules directly inside the correct sector.</span>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-3">{err}</div>}

      <div className="flex gap-4 overflow-x-auto pb-3" data-testid="pest-catalog-sectors">
        {loading && <div className="col-span-full text-center text-slate-400 py-10"><Loader2 className="inline animate-spin" size={20} /></div>}
        {!loading && rows.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No pest types yet.</div>}
        {!loading && ['residential', 'commercial', 'industrial'].map((sector) => <section key={sector} className="min-w-[20rem] flex-1 border border-slate-200 bg-slate-50 p-3"><div className="mb-3 flex items-center justify-between gap-2"><h3 className="text-sm font-bold text-slate-900">{sector[0].toUpperCase() + sector.slice(1)} Pest Catalog & Warranty Rules</h3><Button onClick={() => setEditing({ __new: true, sector })} className="bg-emerald-700 hover:bg-emerald-800" size="sm" data-testid={`pest-new-${sector}`}><Plus size={13} className="mr-1" />Add Pest</Button></div>{rows.filter((p) => (p.sector || 'residential') === sector).map(p => (
          <div key={p.id} className={`bg-white border rounded-xl p-3.5 ${p.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`} data-testid={`pest-card-${p.slug}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{p.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{p.slug}</div>
              </div>
              <button onClick={() => toggleActive(p)} className={`text-xs font-semibold flex items-center gap-1 ${p.active ? 'text-emerald-700' : 'text-slate-400'}`} data-testid={`pest-toggle-${p.slug}`}>
                {p.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div className="bg-slate-50 rounded p-2">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Warranty</div>
                <div className="text-sm font-bold text-slate-900">{p.warranty_value} {p.warranty_unit}</div>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">GST</div>
                <div className="text-sm font-bold text-slate-900">{p.gst_rate}%</div>
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setEditing(p)} className="flex-1" data-testid={`pest-edit-${p.slug}`}><Edit2 size={12} className="mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-rose-600 hover:bg-rose-50" data-testid={`pest-del-${p.slug}`}><Trash2 size={12} /></Button>
            </div>
          </div>
        ))}{!loading && rows.filter((p) => (p.sector || 'residential') === sector).length === 0 && <p className="py-5 text-center text-xs text-slate-400">No pest rules in this category.</p>}</section>)}
      </div>

      {editing && <PestFormModal api={api} initial={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function PestFormModal({ api, initial, onClose, onSaved }) {
  const isNew = !initial || initial.__new;
  const [form, setForm] = useState(() => initial || {
    name: '', sector: initial?.sector || 'residential', warranty_value: 30, warranty_unit: 'days', gst_rate: 18, active: true,
    base_rate: '', per_sqft_rate: '', min_charge: '',
    bhk_rates: { '1bhk': '', '2bhk': '', '3bhk': '', '4bhk': '', villa: '' },
    drilling_rate_per_point: '', emergency_surcharge: '', travel_charge_per_km: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { setErr('Name is required'); return; }
    // sanitise empty strings → null for optional numeric fields, BHK rates → kept as numbers
    const out = { ...form };
    ['base_rate', 'per_sqft_rate', 'min_charge', 'drilling_rate_per_point', 'emergency_surcharge', 'travel_charge_per_km'].forEach(k => {
      if (out[k] === '' || out[k] === null) out[k] = null; else out[k] = Number(out[k]);
    });
    out.bhk_rates = Object.fromEntries(
      Object.entries(form.bhk_rates || {}).filter(([, v]) => v !== '' && v !== null).map(([k, v]) => [k, Number(v)])
    );
    setBusy(true); setErr('');
    try {
      if (isNew) await api('POST', '/super-admin/pest-catalog', out);
      else await api('PATCH', `/super-admin/pest-catalog/${initial.id}`, out);
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="pest-form-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden flex flex-col max-h-[94vh]">
        <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-700 text-white">
          <div><div className="text-xs opacity-90">Pest Catalog & Pricing Rules</div><div className="font-bold">{isNew ? 'Add Pest Type' : `Edit ${initial.name}`}</div></div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2"><div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Spider Control" data-testid="pest-form-name" /></div><div><Label>Category</Label><select value={form.sector || 'residential'} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="pest-form-sector"><option value="residential">Residential</option><option value="commercial">Commercial</option><option value="industrial">Industrial</option></select></div></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Warranty</Label><Input type="number" min="0" value={form.warranty_value} onChange={(e) => setForm({ ...form, warranty_value: Number(e.target.value) })} data-testid="pest-form-val" /></div>
            <div><Label>Unit</Label>
              <select value={form.warranty_unit} onChange={(e) => setForm({ ...form, warranty_unit: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="pest-form-unit">
                <option value="days">days</option><option value="months">months</option><option value="years">years</option>
              </select>
            </div>
            <div><Label>GST %</Label><Input type="number" step="0.5" min="0" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} data-testid="pest-form-gst" /></div>
          </div>

          <details open className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <summary className="text-sm font-semibold text-slate-700 cursor-pointer">💰 Pricing Matrix (used in Invoice auto-fill)</summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Base Rate ₹</Label><Input type="number" min="0" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} placeholder="flat fee" data-testid="pest-base" /></div>
                <div><Label className="text-xs">Per Sq.Ft ₹</Label><Input type="number" min="0" step="0.1" value={form.per_sqft_rate} onChange={(e) => setForm({ ...form, per_sqft_rate: e.target.value })} placeholder="for area-based" data-testid="pest-sqft" /></div>
                <div><Label className="text-xs">Minimum Charge ₹</Label><Input type="number" min="0" value={form.min_charge} onChange={(e) => setForm({ ...form, min_charge: e.target.value })} placeholder="price floor" data-testid="pest-min" /></div>
              </div>
              <div>
                <Label className="text-xs">Property-Size Rates (overrides base/sqft when picked)</Label>
                <div className="grid grid-cols-5 gap-2 mt-1">
                  {['1bhk', '2bhk', '3bhk', '4bhk', 'villa'].map(k => (
                    <div key={k}>
                      <div className="text-[10px] text-slate-500 uppercase font-semibold mb-0.5">{k.toUpperCase()}</div>
                      <Input type="number" min="0" value={form.bhk_rates?.[k] || ''} onChange={(e) => setForm({ ...form, bhk_rates: { ...(form.bhk_rates || {}), [k]: e.target.value } })} className="h-9 text-sm" data-testid={`pest-bhk-${k}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Drilling ₹/point</Label><Input type="number" min="0" value={form.drilling_rate_per_point} onChange={(e) => setForm({ ...form, drilling_rate_per_point: e.target.value })} data-testid="pest-drill" /></div>
                <div><Label className="text-xs">Emergency ₹</Label><Input type="number" min="0" value={form.emergency_surcharge} onChange={(e) => setForm({ ...form, emergency_surcharge: e.target.value })} data-testid="pest-emerg" /></div>
                <div><Label className="text-xs">Travel ₹/km</Label><Input type="number" min="0" step="0.5" value={form.travel_charge_per_km} onChange={(e) => setForm({ ...form, travel_charge_per_km: e.target.value })} data-testid="pest-travel" /></div>
              </div>
              <div><Label className="text-xs">Internal Notes</Label><Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g., includes 2 follow-ups" data-testid="pest-notes" /></div>
            </div>
          </details>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="pest-form-active" />
            <span>Active (visible in dropdowns)</span>
          </label>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="pest-form-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (isNew ? 'Add Pest' : 'Save')}
          </Button>
        </div>
      </form>
    </div>
  );
}


// =====================================================
// MANAGE WARRANTY MODAL — extend / reduce / cancel / stop / reactivate
// Every action requires a `reason` that the customer SEES on their portal.
// =====================================================
function ManageWarrantyModal({ api, warranty, onClose, onChanged }) {
  const [action, setAction] = useState('extend');
  const [value, setValue] = useState(1);
  const [unit, setUnit] = useState('months');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const ACTIONS = [
    { key: 'extend',     label: 'Extend Coverage',     desc: 'Add more days to warranty', tone: 'emerald' },
    { key: 'reduce',     label: 'Reduce Coverage',     desc: 'Cut warranty period short', tone: 'amber' },
    { key: 'stop',       label: 'Stop / Pause',        desc: 'Pause due to non-payment etc.', tone: 'amber' },
    { key: 'cancel',     label: 'Cancel Warranty',     desc: 'Permanently void this warranty', tone: 'rose' },
    { key: 'reactivate', label: 'Reactivate',          desc: 'Restore a paused/cancelled warranty', tone: 'emerald' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setErr('Please provide a reason (it will be shown to the customer)'); return; }
    if ((action === 'extend' || action === 'reduce') && (!value || value <= 0)) { setErr('Enter a positive value'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { action, reason };
      if (action === 'extend' || action === 'reduce') { payload.value = Number(value); payload.unit = unit; }
      await api('POST', `/admin/warranties/${warranty.id}/manage`, payload);
      onChanged();
    } catch (e2) { setErr(e2.response?.data?.detail || e2.message); }
    finally { setBusy(false); }
  };

  const history = warranty.status_history || [];

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="war-manage-modal">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-xl rounded-2xl overflow-hidden max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 bg-indigo-700 text-white">
          <div>
            <div className="text-xs opacity-90 flex items-center gap-1"><Settings size={12} /> Manage Warranty</div>
            <div className="font-bold">{warranty.warranty_number}</div>
            <div className="text-xs opacity-90">{warranty.customer_name} · {warranty.pest_name}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          {/* Current state */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-slate-50 rounded-md p-2"><div className="uppercase text-slate-500">Status</div><div className="font-bold mt-1"><StatusBadge status={warranty.status} /></div></div>
            <div className="bg-slate-50 rounded-md p-2"><div className="uppercase text-slate-500">Valid Until</div><div className="font-bold mt-1">{fmtDate(warranty.end_date)}</div></div>
            <div className="bg-slate-50 rounded-md p-2"><div className="uppercase text-slate-500">Coverage</div><div className="font-bold mt-1">{warranty.warranty_value} {warranty.warranty_unit}</div></div>
          </div>

          {/* Action chooser */}
          <div>
            <Label className="text-xs font-semibold">Action *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
              {ACTIONS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setAction(a.key)}
                  className={`text-left p-2.5 rounded-lg border text-sm transition ${action === a.key ? `border-${a.tone}-500 bg-${a.tone}-50 ring-2 ring-${a.tone}-200` : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid={`war-action-${a.key}`}
                >
                  <div className="font-bold text-slate-900">{a.label}</div>
                  <div className="text-xs text-slate-500">{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Numeric value for extend/reduce */}
          {(action === 'extend' || action === 'reduce') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} className="h-10" data-testid="war-mng-value" />
              </div>
              <div>
                <Label className="text-xs">Unit</Label>
                <select value={unit} onChange={(e) => setUnit(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="war-mng-unit">
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
          )}

          {/* Reason — REQUIRED + customer-visible */}
          <div>
            <Label className="text-xs font-semibold flex items-center gap-1">
              Reason * <span className="text-[10px] uppercase tracking-wide text-indigo-700">(visible to customer)</span>
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g., Customer pending payment of ₹2,000 — warranty paused until cleared"
              className="mt-1 w-full p-2 rounded-md border border-slate-300 text-sm"
              data-testid="war-mng-reason"
            />
          </div>

          {/* Status history timeline */}
          {history.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <div className="text-xs font-bold uppercase text-slate-500 mb-1.5 flex items-center gap-1"><History size={12} /> Change History</div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {history.slice().reverse().map((h, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold capitalize text-slate-800">{h.action}</span>
                      <span className="text-[10px] text-slate-500"><Clock size={9} className="inline mr-0.5" /> {new Date(h.ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-slate-600 mt-0.5">{h.reason}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">by {h.actor}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-2" data-testid="war-mng-error"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" className="bg-indigo-700 hover:bg-indigo-800" disabled={busy} data-testid="war-mng-submit">
            {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</> : 'Apply & Notify Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
